import {
	type Cashout,
	type DownloadedFile,
	type PortalMoney,
	type PortalStatement,
	type StorePayout,
	saveFile,
} from "@khazzin/api-client";
import type { Locale, Translate } from "@khazzin/i18n";
import { formatDate } from "@khazzin/i18n/dates";
import { formatMoney } from "@khazzin/i18n/money";
import { type DateRange, DateRangePicker, presetRange, todayIn } from "@khazzin/ui/patterns";
import {
	EmptyState,
	ErrorState,
	KeyValueList,
	KpiTile,
	PageHeader,
} from "@khazzin/ui/patterns/page";
import { StatusChip } from "@khazzin/ui/patterns/StatusChip";
import { type Column, DataTable, useCursorPagination } from "@khazzin/ui/patterns/table";
import { Button, SkeletonText, Tabs } from "@khazzin/ui/primitives";
import type { Tone } from "@khazzin/ui/styles";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";
import { useApi } from "../../api";
import { useLocale } from "../../app/shell/locale";
import { NotFound } from "../../app/shell/Shell";
import { useStoreScope } from "../../app/shell/scope";
import { levelOf } from "../../app/shell/sections";
import { CashoutRequestsTable, RequestCashoutButton } from "./CashoutRequests";
import { ExpressCashoutDialog } from "./ExpressCashoutDialog";

/**
 * The Store Portal's Finance (EP10-S08, DA-10-09): the period's seven totals,
 * the balance card, and the Cashouts / Statements / Payouts / Storage fee tabs.
 *
 * **Read-only at `finance = read`.** The writes — Request cashout and Cancel
 * (EP10-S09), "Cash out now" (EP04-S08), and Dispute on a statement line — are
 * shown only at `write`. Every figure is the server's — the page adds nothing
 * up, so what it shows is what reconciles.
 *
 * PDFs are fetched with the bearer token and saved from a blob: a bare link
 * would carry no Authorization header.
 */

export const FINANCE_TABS = ["cashouts", "statements", "payouts", "storage"] as const;
export type FinanceTab = (typeof FINANCE_TABS)[number];

const CASHOUT_TONES: Record<Cashout["status"], Tone> = {
	generating: "info",
	generated: "info",
	in_run: "info",
	paid: "success",
	cancelled: "neutral",
};

export function financeKey(storeId: string) {
	return ["portal", storeId, "finance"] as const;
}

export function money(value: PortalMoney | undefined, locale: Locale): string {
	return value === undefined ? "—" : formatMoney(value, locale);
}

/** A list the server answers whole, as the table's one page. */
function wholePage<Row>(items: Row[] | undefined) {
	const count = items?.length ?? 0;
	return {
		page: { items: items ?? [], nextCursor: null },
		pagination: {
			showingFrom: count === 0 ? 0 : 1,
			showingTo: count,
			hasNext: false,
			hasPrevious: false,
			onNext: () => {},
			onPrevious: () => {},
		},
	};
}

/** Fetches a file and hands it to the browser, reporting a failure in place. */
export function useDownload(t: Translate) {
	const [failed, setFailed] = useState<string | null>(null);
	// ADJ-M03-4: the document being fetched, so its button is busy and a
	// second press does not fetch it twice.
	const [busy, setBusy] = useState<string | null>(null);
	return {
		failed,
		busy,
		download: async (key: string, fetchFile: () => Promise<DownloadedFile>) => {
			if (busy === key) {
				return;
			}
			setBusy(key);
			setFailed(null);
			try {
				saveFile(await fetchFile());
			} catch {
				setFailed(t("portal.finance.downloadFailed"));
			} finally {
				setBusy(null);
			}
		},
	};
}

export function StatementStatusChip({
	statement,
	t,
}: {
	statement: PortalStatement;
	t: Translate;
}) {
	return statement.status === "provisional" ? (
		<StatusChip tone="warning">{t("portal.finance.status.provisional")}</StatusChip>
	) : (
		<StatusChip tone={statement.settled ? "success" : "neutral"}>
			{t(statement.settled ? "portal.finance.status.settled" : "portal.finance.status.final")}
		</StatusChip>
	);
}

export function FinancePage() {
	const { t, locale } = useLocale();
	const { store, context } = useStoreScope();
	const api = useApi();
	const [params, setParams] = useSearchParams();
	const timeZone = context?.preferences?.timezone ?? "Asia/Beirut";
	const today = todayIn(timeZone);
	const [range, setRange] = useState<DateRange>(() => presetRange("thisMonth", today));
	const [express, setExpress] = useState(false);
	const storeId = store?.storeId ?? "";
	const level = levelOf(store, "finance");
	const raw = params.get("tab");
	const tab: FinanceTab = FINANCE_TABS.find((value) => value === raw) ?? "cashouts";

	const summary = useQuery({
		queryKey: [...financeKey(storeId), "summary", range.from, range.to],
		queryFn: ({ signal }) => api.getPortalFinanceSummary(range, { signal }),
		enabled: storeId !== "" && level !== "none",
	});
	const balance = useQuery({
		queryKey: [...financeKey(storeId), "balance"],
		queryFn: ({ signal }) => api.getPortalFinanceBalance({ signal }),
		enabled: storeId !== "" && level !== "none",
	});

	if (store !== null && level === "none") {
		return <NotFound />;
	}

	const s = summary.data;
	const status = summary.isLoading ? "loading" : summary.isError ? "error" : "ready";
	const tiles: [string, string][] = [
		["orders", s === undefined ? "—" : String(s.orders)],
		["collected", money(s?.collected, locale)],
		["deliveryCharges", money(s?.deliveryCharges, locale)],
		["fulfillmentCharges", money(s?.fulfillmentCharges, locale)],
		["packaging", money(s?.packaging, locale)],
		["storage", money(s?.storage, locale)],
		["cashoutsPaid", money(s?.cashoutsPaid, locale)],
	];
	const b = balance.data;

	return (
		<div className="flex flex-col gap-4">
			<PageHeader title={t("portal.finance.title")} subtitle={t("portal.finance.subtitle")} />
			<DateRangePicker value={range} onChange={setRange} today={today} t={t} />
			<section
				aria-label={t("portal.finance.kpis")}
				className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7"
			>
				{tiles.map(([key, value]) => (
					<KpiTile
						key={key}
						label={t(`portal.finance.kpi.${key}`)}
						value={value}
						status={status}
						error={summary.error}
						onRetry={() => void summary.refetch()}
						t={t}
					/>
				))}
			</section>
			<section
				aria-label={t("portal.finance.balance.title")}
				className="flex flex-col gap-3 card p-4 md:flex-row md:items-center md:justify-between"
			>
				{balance.isLoading ? (
					<SkeletonText lines={3} />
				) : balance.isError || b === undefined ? (
					<ErrorState error={balance.error} onRetry={() => void balance.refetch()} t={t} compact />
				) : (
					<KeyValueList
						className="flex-1"
						rows={[
							{ label: t("portal.finance.balance.live"), value: money(b.balance, locale) },
							{ label: t("portal.finance.balance.available"), value: money(b.available, locale) },
							{
								label: t("portal.finance.balance.nextPayout"),
								value:
									b.nextPayoutOn === undefined
										? t("portal.finance.balance.onRequest")
										: formatDate(b.nextPayoutOn, { locale, timeZone: "UTC" }),
							},
						]}
					/>
				)}
				{level === "write" && storeId !== "" && (
					<div className="flex flex-col gap-2 md:flex-row">
						{b !== undefined && <RequestCashoutButton available={b.available} />}
						<Button
							variant="secondary"
							className="min-h-12 md:min-h-8"
							onClick={() => setExpress(true)}
						>
							{t("portal.finance.express.title")}
						</Button>
					</div>
				)}
			</section>
			{express && (
				<ExpressCashoutDialog storeId={storeId} open={express} onOpenChange={setExpress} />
			)}
			<Tabs
				aria-label={t("portal.finance.tabs.label")}
				value={tab}
				onValueChange={(value) =>
					setParams(value === "cashouts" ? {} : { tab: value }, { replace: true })
				}
				tabs={FINANCE_TABS.map((value) => ({ value, label: t(`portal.finance.tabs.${value}`) }))}
			/>
			{storeId !== "" && tab === "cashouts" && (
				<>
					<CashoutRequestsTable
						storeId={storeId}
						timeZone={timeZone}
						canWrite={level === "write"}
					/>
					<CashoutsTab storeId={storeId} timeZone={timeZone} />
				</>
			)}
			{storeId !== "" && tab === "statements" && (
				<StatementsTab storeId={storeId} timeZone={timeZone} />
			)}
			{storeId !== "" && tab === "payouts" && <PayoutsTab storeId={storeId} timeZone={timeZone} />}
			{storeId !== "" && tab === "storage" && <StorageFeeTab storeId={storeId} />}
		</div>
	);
}

function CashoutsTab({ storeId, timeZone }: { storeId: string; timeZone: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const { failed, busy, download } = useDownload(t);
	const page = useCursorPagination<Cashout>(
		(cursor, limit, signal) =>
			api
				.listPortalCashouts({ ...(cursor === null ? {} : { cursor }), limit, signal })
				.then((answer) => ({ items: answer.items, nextCursor: answer.nextCursor ?? null })),
		{ queryKey: [...financeKey(storeId), "cashouts"] },
	);
	const columns: Column<Cashout>[] = [
		{
			id: "number",
			headerKey: "portal.finance.cashouts.number",
			mono: true,
			card: "wide",
			cell: (c) => c.number,
		},
		{
			id: "orders",
			headerKey: "portal.finance.cashouts.orders",
			align: "end",
			cell: (c) => c.orderIds.length,
		},
		{
			id: "net",
			headerKey: "portal.finance.cashouts.net",
			align: "end",
			cell: (c) => formatMoney({ amountMinor: c.netPayableMinor, currency: c.currency }, locale),
		},
		{
			id: "status",
			headerKey: "portal.finance.cashouts.status",
			cell: (c) => (
				<StatusChip tone={CASHOUT_TONES[c.status]}>
					{t(`portal.finance.cashouts.state.${c.status}`)}
				</StatusChip>
			),
		},
		{
			id: "paid",
			headerKey: "portal.finance.cashouts.paid",
			cell: (c) => (c.paidAt ? formatDate(c.paidAt, { locale, timeZone }) : "—"),
		},
		{
			id: "pdf",
			headerKey: "portal.finance.cashouts.document",
			cell: (c) => (
				<Button
					size="sm"
					variant="secondary"
					aria-label={t("portal.finance.pdfOf", { name: c.number })}
					loading={busy === c.id}
					onClick={() => void download(c.id, () => api.downloadPortalCashoutDocument(c.id))}
				>
					{t("portal.finance.pdf")}
				</Button>
			),
		},
	];
	return (
		<div className="flex flex-col gap-2">
			{failed !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failed}
				</p>
			)}
			<DataTable<Cashout>
				screenKey="portal.finance.cashouts"
				caption={t("portal.finance.tabs.cashouts")}
				t={t}
				columns={columns}
				rowKey={(c) => c.id}
				page={{ items: page.page?.items ?? [], nextCursor: page.page?.nextCursor ?? null }}
				status={page.status}
				error={page.error}
				onRetry={page.refetch}
				pagination={{
					showingFrom: page.showingFrom,
					showingTo: page.showingTo,
					hasNext: page.hasNext,
					hasPrevious: page.hasPrevious,
					onNext: page.next,
					onPrevious: page.previous,
				}}
				empty={<EmptyState title={t("portal.finance.cashouts.empty")} illustration="none" />}
			/>
		</div>
	);
}

function StatementsTab({ storeId, timeZone }: { storeId: string; timeZone: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const { failed, busy, download } = useDownload(t);
	const list = useQuery({
		queryKey: [...financeKey(storeId), "statements"],
		queryFn: ({ signal }) => api.listPortalStatements({ signal }),
	});
	const day = (value: string | undefined) =>
		value ? formatDate(value, { locale, timeZone }) : "…";
	const columns: Column<PortalStatement>[] = [
		{
			id: "period",
			headerKey: "portal.finance.statements.period",
			card: "wide",
			cell: (st) => (
				<Link
					to={`/finance/statements/${encodeURIComponent(st.statementId)}`}
					className="font-semibold text-brand-ink underline-offset-2 hover:underline"
				>
					{`${day(st.periodStart)} – ${day(st.periodEnd)}`}
				</Link>
			),
		},
		{
			id: "opening",
			headerKey: "portal.finance.statements.opening",
			align: "end",
			cell: (st) => money(st.openingBalance, locale),
		},
		{
			id: "closing",
			headerKey: "portal.finance.statements.closing",
			align: "end",
			cell: (st) => money(st.closingBalance, locale),
		},
		{
			id: "status",
			headerKey: "portal.finance.statements.status",
			cell: (st) => <StatementStatusChip statement={st} t={t} />,
		},
		{
			id: "pdf",
			headerKey: "portal.finance.statements.document",
			cell: (st) =>
				st.hasDocument ? (
					<Button
						size="sm"
						variant="secondary"
						aria-label={t("portal.finance.pdfOf", { name: day(st.periodStart) })}
						loading={busy === st.statementId}
						onClick={() =>
							void download(st.statementId, () =>
								api.downloadPortalStatementDocument(st.statementId),
							)
						}
					>
						{t("portal.finance.pdf")}
					</Button>
				) : (
					"—"
				),
		},
	];
	const whole = wholePage(list.data?.items);
	return (
		<div className="flex flex-col gap-2">
			{failed !== null && (
				<p role="alert" className="text-[12.5px] text-danger-fg">
					{failed}
				</p>
			)}
			<DataTable<PortalStatement>
				screenKey="portal.finance.statements"
				caption={t("portal.finance.tabs.statements")}
				t={t}
				columns={columns}
				rowKey={(st) => st.statementId}
				page={whole.page}
				status={list.isLoading ? "loading" : list.isError ? "error" : "success"}
				error={list.error}
				onRetry={() => void list.refetch()}
				pagination={whole.pagination}
				empty={<EmptyState title={t("portal.finance.statements.empty")} illustration="none" />}
			/>
		</div>
	);
}

function PayoutsTab({ storeId, timeZone }: { storeId: string; timeZone: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const list = useQuery({
		queryKey: [...financeKey(storeId), "payouts"],
		queryFn: ({ signal }) => api.listPortalPayouts({ signal }),
	});
	const columns: Column<StorePayout>[] = [
		{
			id: "cashout",
			headerKey: "portal.finance.payouts.cashout",
			mono: true,
			card: "wide",
			cell: (p) => p.cashoutNumber,
		},
		{
			id: "amount",
			headerKey: "portal.finance.payouts.amount",
			align: "end",
			cell: (p) => formatMoney({ amountMinor: p.amountMinor, currency: p.currency }, locale),
		},
		{
			id: "paid",
			headerKey: "portal.finance.payouts.paid",
			cell: (p) => formatDate(p.paidAt, { locale, timeZone }),
		},
	];
	const whole = wholePage(list.data?.items);
	return (
		<DataTable<StorePayout>
			screenKey="portal.finance.payouts"
			caption={t("portal.finance.tabs.payouts")}
			t={t}
			columns={columns}
			rowKey={(p) => p.cashoutId}
			page={whole.page}
			status={list.isLoading ? "loading" : list.isError ? "error" : "success"}
			error={list.error}
			onRetry={() => void list.refetch()}
			pagination={whole.pagination}
			empty={<EmptyState title={t("portal.finance.payouts.empty")} illustration="none" />}
		/>
	);
}

/** The four a store's storage fee always names, in the reader's language. */
const STANDARD_KEYS: readonly string[] = ["cbm", "sut_std_small_bin", "sut_std_large_bin", "shelf"];

function StorageFeeTab({ storeId }: { storeId: string }) {
	const { t, locale } = useLocale();
	const api = useApi();
	const card = useQuery({
		queryKey: [...financeKey(storeId), "storage"],
		queryFn: ({ signal }) => api.getPortalStorageFee({ signal }),
		retry: false,
	});
	if (card.isLoading) {
		return <SkeletonText lines={4} />;
	}
	if (card.isError || card.data === undefined) {
		return <EmptyState title={t("portal.finance.storage.unpriced")} illustration="none" />;
	}
	const row = card.data;
	const fee = row.current;
	const currency = fee?.currency ?? row.currency;
	const price = (amountMinor: number | null | undefined) =>
		amountMinor === undefined || amountMinor === null
			? "—"
			: formatMoney({ amountMinor, currency }, locale);
	const occupancy = row.occupancy;
	// A CBM position, a shelf, and the Small and Large bin standard units in
	// the reader's language; the warehouse's own kinds of bin (owner note 14)
	// and bin units by the names they come with.
	const unitName = (kind: string) =>
		STANDARD_KEYS.includes(kind)
			? t(`portal.finance.storage.unit.${kind}` as Parameters<typeof t>[0])
			: (occupancy?.unitsByKind.find((u) => u.unitKind === kind)?.name ??
				fee?.binRates?.find((r) => r.storageUnitTemplateId === kind)?.name ??
				kind);
	const occupied =
		occupancy === undefined || !occupancy.available
			? t("portal.finance.storage.unknown")
			: occupancy.unitsByKind
					.filter((u) => u.units > 0)
					.map((u) => `${u.units} × ${unitName(u.unitKind)}`)
					.join(", ") || "0";
	return (
		<section aria-label={t("portal.finance.tabs.storage")} className="flex flex-col gap-3 card p-4">
			<KeyValueList
				rows={[
					{
						label: t("portal.finance.storage.price"),
						value:
							fee === undefined
								? t("portal.finance.storage.startsLater")
								: fee.pricingModel === "PER_OCCUPIED_UNIT_MONTH"
									? [
											`${price(fee.rateCbmMinor)} / ${unitName("cbm")}`,
											// Every bin unit's rate: the Small bin and the Large
											// bin first, then the warehouse's own.
											...(fee.binRates ?? []).map(
												(rate) =>
													`${price(rate.rateMinor)} / ${unitName(rate.storageUnitTemplateId)}`,
											),
											`${price(fee.rateShelfMinor)} / ${unitName("shelf")}`,
											...(fee.customRates ?? []).map(
												(rate) => `${price(rate.rateMinor)} / ${rate.name ?? rate.unitKind}`,
											),
										].join(" · ")
									: price(fee.amountMinor),
					},
					{ label: t("portal.finance.storage.occupied"), value: occupied },
					{
						label: t("portal.finance.storage.monthToDate"),
						value: row.accruedKnown
							? price(row.accruedThisMonthMinor)
							: t("portal.finance.storage.unknown"),
					},
				]}
			/>
			<p className="text-[12.5px] text-fg-muted">{t("portal.finance.storage.readOnly")}</p>
		</section>
	);
}
